import { useMutation } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { ConversionTableResource } from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

type CreateConversionTablePayload = {
  name: string;
  destinationGradeType: 'numeric' | 'letter';
  gradeSchemaId: string;
  sourceInstitutionName?: string;
  sourceCountryCode?: string;
  sourceEducationLevel?: string;
  courseId?: string;
  rules: ConversionTableResource['attributes']['rules'];
};

type CreateConversionTableResponse = {
  data: ConversionTableResource;
};

export function useCreateConversionTable() {
  const request = useRequest();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async (payload: CreateConversionTablePayload) => {
      const response = await request<CreateConversionTableResponse>(`${config.apiHost}/private/v1/conversion_tables`, {
        method: 'POST',
        body: {
          data: {
            type: 'conversion_table',
            attributes: {
              name: payload.name,
              destinationGradeType: payload.destinationGradeType,
              gradeSchema: payload.gradeSchemaId,
              sourceInstitutionName: payload.sourceInstitutionName,
              sourceCountryCode: payload.sourceCountryCode,
              sourceEducationLevel: payload.sourceEducationLevel,
              course: payload.courseId,
              rules: payload.rules,
            },
          },
        },
        isExpectedResponse,
      });

      return response.data;
    },
  });

  return {
    createConversionTable: mutateAsync,
    isCreatingConversionTable: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is CreateConversionTableResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
